import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, FormControl, Grid, Group, Layout, Page } from "@/comn/components";
import ReactECharts from "echarts-for-react";
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    UseGridReturn,
} from "@/comn/hooks";
import {
    APIS,
    SF_SURV_MGMT_LIST_SRCH,
    SF_SURV_STSTCL_INFO,
    SG_SURV_MGMT_LIST,
    SG_SURV_STSTCL_LIST,
    SG_SURV_STSTCL_SUB_LIST,
    URLS,
} from "./services/PtliSurvStstclService";
import { forEach } from "lodash";

/*
 * Survey Statistics Detail
 * !== 설문 통계 상세 ==!
 */
export const PTLI0422002Q = (props: any): JSX.Element => {
    const { callback } = props;
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const { qestnrId, quesCnt } = useParams();

    const [survStstclListData, setSurvStstclListData] = useState([]);

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survInfo: useForm({
            defaultSchema: SF_SURV_STSTCL_INFO,
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */

    // 그리드 선언부
    // 객관식
    const gridPool = Array.from({ length: 10 }, () => useGrid({ defaultSchema: SG_SURV_STSTCL_LIST }));
    const grid = Object.fromEntries(gridPool.map((g, i) => [`survststclList${String(i + 1).padStart(2, "0")}`, g]));

    const gridPoolSub = Array.from({ length: 5 }, () => useGrid({ defaultSchema: SG_SURV_STSTCL_SUB_LIST }));
    const gridSub = Object.fromEntries(
        gridPoolSub.map((g, i = 10) => [`survststclList${String(i + 1).padStart(2, "0")}`, g]),
    );

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getSurvInfo: useFetch({
            api: () => APIS.getSurvInfo(qestnrId),
            enabled: !!qestnrId,
            onSuccess: (data) => {
                const survInfo = data.survInfo.content;
                form.survInfo.setValues(survInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),

        getSurvStstclList: useFetch({
            api: (data: any) => APIS.getSurvStstclList(data),
            onSuccess: (data) => {
                setSurvStstclListData(data.survMap.content);
            },
            onError: () => {},
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        getSurvStstclList: async (props: any) => {
            await fetch.getSurvStstclList.fetch(props);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getSurvInfo.fetch();
        form.survInfo.setEditable(false);

        const props = { qestnrId: qestnrId, quesCnt: quesCnt };
        handler.getSurvStstclList(props);
    }, []);

    useEffect(() => {}, [survStstclListData]);

    return (
        <Page>
            <Group>
                <Group.Header>
                    <Group.Title title={"T_SURV_INFO"} />
                </Group.Header>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.bsopClsfCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.ptlTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTtle}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrPupsCn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.reqDateRnge}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            {survStstclListData.map((surv: any, idx: number) => {
                const keySuffix = String(idx + 1).padStart(2, "0");
                const gridKey = `survststclList${keySuffix}`;
                const dataKey = `survStstclListData${keySuffix}`;

                // 객관식
                const gridInstance = grid[gridKey];
                // 주관식
                const gridSubInstance = gridSub[gridKey];

                const dataPacket = surv[dataKey];

                if (!gridInstance || !dataPacket) return null;

                const data = dataPacket.content
                    .filter((data: any) => {
                        return data.quesItmId != null;
                    })
                    .map((data: any) => {
                        return { name: data.itmCn + ` (${data.rspnPer})`, value: data.rspnCnt };
                    });

                return (
                    (dataPacket.content[0].quesTpCd == "1" && (
                        <Layout key={`layout-${idx}`}>
                            <Group>
                                <Group.Body>
                                    <Group.Section>
                                        <ReactECharts
                                            option={{
                                                title: {
                                                    text: dataPacket.content[0].quesCn,
                                                    left: "center",
                                                },
                                                tooltip: {
                                                    trigger: "item",
                                                },
                                                series: [
                                                    {
                                                        name: "Access From",
                                                        type: "pie",
                                                        radius: "60%",
                                                        data: data,
                                                        emphasis: {
                                                            itemStyle: {
                                                                shadowBlur: 10,
                                                                shadowOffsetX: 0,
                                                                shadowColor: "rgba(0, 0, 0, 0.5)",
                                                            },
                                                        },
                                                    },
                                                ],
                                            }}
                                        />
                                    </Group.Section>
                                </Group.Body>
                            </Group>
                            <Group>
                                <Group.Body>
                                    <Group.Section>
                                        <Grid {...gridInstance.grid} key={`grid-${idx}`} data={dataPacket} />
                                    </Group.Section>
                                </Group.Body>
                            </Group>
                        </Layout>
                    )) || (
                        <Layout key={`layout-${idx}`}>
                            <Group>
                                <Group.Header>
                                    <Group.Title title={dataPacket.content[0].quesCn} />
                                </Group.Header>
                                <Group.Body>
                                    <Group.Section>
                                        <Grid {...gridSubInstance.grid} key={`grid-${idx}`} data={dataPacket} />
                                    </Group.Section>
                                </Group.Body>
                            </Group>
                        </Layout>
                    )
                );
            })}

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0422001q);
                        }}
                    ></Button>
                </Layout.Left>
            </Layout>
        </Page>
    );
};
